'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { HospitalityGapError, useHospitalitySource } from '@/src/lib/hospitality/hospitalitySource';
import { useRestaurantSite } from '@/src/lib/hospitality/useRestaurantSite';
import type { Menu, MenuItem, Orderability, RestaurantSite } from '@/src/lib/hospitality/wire';
import type {
  AddLineInput,
  Cart,
  CheckoutInput,
  DeliveryAddressInput,
  DinerFulfilmentType,
  PlacedOrder,
} from '@/src/lib/hospitality/orderWire';
import { formatMoney } from '@/src/lib/money';
import { BlockSection, EmptyNote, SiteStateNotice, useActiveLocale, useHospitalityMessages } from './shared';
import { CartPanel } from './order/CartPanel';
import { CheckoutForm } from './order/CheckoutForm';
import { FulfilmentControls } from './order/Fulfilment';
import { ModifierDialog } from './order/ModifierDialog';
import { formatInZone, productIdsToRecheck, unorderableLines } from './order/model';

/**
 * The ordering block: a menu, a cart, a fulfilment choice and a checkout.
 *
 * <h3>There is no client-side cart</h3>
 *
 * <p>`Cart` and `CartLine` are the cart. Every add, quantity change, removal and
 * fulfilment switch is a call to the ANONYMOUS phase-6 sale API through this
 * app's own BFF, and the state below holds whatever the last response said —
 * never a locally-mutated copy. Modifiers are `CartLine` rows with
 * `lineType = MODIFIER`, `parentLine` and the group's `slotKey`, exactly as the
 * till writes them. The cart's token is a bearer credential and lives in an
 * `HttpOnly` cookie; nothing here has ever seen it.
 *
 * <h3>The cut-off re-check is the guard that matters</h3>
 *
 * <p>Before checkout opens, every distinct product in the cart is re-asked
 * through `orderable(...)`. A line that went unorderable while the diner
 * browsed is flagged IN PLACE with its reason and the checkout button refuses —
 * it is never silently dropped, and the diner removes or substitutes it. That
 * is the one behaviour in this block that stops a kitchen being handed a ticket
 * it cannot cook.
 *
 * <h3>What it never does</h3>
 *
 * <p>It creates no `SalesOrder` — `CheckoutResource` converts the cart through
 * `OrderPlacementService`. It validates no delivery address against a postcode,
 * a radius or a `ServiceArea`; "out of area" is `ShippingQuoteService`
 * returning no option. It hard-codes no lead time, currency, country, language,
 * tax rate or timezone: `orderLeadMinutes`, `currency` and `timeZone` come off
 * the site, `taxInclusive` is read per line, and the country code is typed by
 * the diner.
 *
 * <h3>The one gap left</h3>
 *
 * <p>Payment. There is no anonymous payment resource anywhere in
 * `commerce-ws` — no `@Path` under `storefront` or `public/storefront` takes
 * one — so a placed order comes back with its `paymentStatus` and the
 * confirmation says payment is taken at the restaurant. See `order/README.md`.
 */

export interface RestaurantOrderProps {
  heading?: string;
  /** ISO instant to resolve the menu for. Unset means now. */
  at?: string;
  /** Which fulfilment types this site offers. `FulfilmentType` also has
   *  `DIGITAL` and `SERVICE`; neither is orderable from a restaurant page. */
  fulfilmentTypes?: DinerFulfilmentType[];
  /** Set both to make terms acceptance part of checkout — they become
   *  `termsDocumentKey`/`termsDocumentVersion` on the placed order. */
  termsDocumentKey?: string;
  termsDocumentVersion?: string;
  termsHref?: string;
}

type Stage = 'order' | 'checkout' | 'placed';

const DEFAULT_FULFILMENT: DinerFulfilmentType[] = ['COLLECTION', 'TAKEAWAY', 'DELIVERY'];

export function RestaurantOrder({
  heading,
  at,
  fulfilmentTypes,
  termsDocumentKey,
  termsDocumentVersion,
  termsHref,
}: RestaurantOrderProps) {
  const t = useHospitalityMessages();
  const locale = useActiveLocale();
  const source = useHospitalitySource();
  const state = useRestaurantSite();
  const site = state.status === 'ready' ? state.site : null;

  const choices = useMemo(
    () => (fulfilmentTypes?.length ? fulfilmentTypes : DEFAULT_FULFILMENT),
    [fulfilmentTypes],
  );

  const [menu, setMenu] = useState<Menu | null>(null);
  const [menuGap, setMenuGap] = useState(false);
  const [cart, setCart] = useState<Cart | null>(null);
  const [fulfilment, setFulfilment] = useState<DinerFulfilmentType>(choices[0]);
  const [isoDate, setIsoDate] = useState<string | null>(null);
  const [requestedAt, setRequestedAt] = useState<string | null>(null);
  const [dialogItem, setDialogItem] = useState<MenuItem | null>(null);
  const [flagged, setFlagged] = useState<ReadonlyMap<number, Orderability>>(new Map());
  const [stage, setStage] = useState<Stage>('order');
  const [order, setOrder] = useState<PlacedOrder | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Every ordering method, not just the first: they are optional on the
  // contract, and a source that can add a line but not remove one would leave a
  // diner stuck with it.
  const ordering =
    !!source?.startCart &&
    !!source.addLine &&
    !!source.setLineQuantity &&
    !!source.removeLine &&
    !!source.setFulfilment &&
    !!source.checkout;

  // The menu. A thrown `HospitalityGapError` is a missing public endpoint, not
  // a broken site: the block says ordering is not available here.
  useEffect(() => {
    if (!source || !site) return;
    let cancelled = false;
    source
      .menu(at)
      .then((loaded) => {
        if (!cancelled) setMenu(loaded);
      })
      .catch((failure: unknown) => {
        if (cancelled) return;
        setMenu(null);
        setMenuGap(failure instanceof HospitalityGapError);
        console.error('[RestaurantOrder] menu', failure);
      });
    return () => {
      cancelled = true;
    };
  }, [source, site, at]);

  // Any cart this visitor already has. `null` is normal, not an error.
  useEffect(() => {
    if (!source?.cart) return;
    let cancelled = false;
    source
      .cart()
      .then((existing) => {
        if (!cancelled && existing) setCart(existing);
      })
      .catch((failure: unknown) => console.error('[RestaurantOrder] cart', failure));
    return () => {
      cancelled = true;
    };
  }, [source]);

  // Today, in the RESTAURANT's zone. The visitor's own midnight is the wrong
  // one to open a collection picker on.
  useEffect(() => {
    if (!site || isoDate) return;
    setIsoDate(todayIn(site.timeZone));
  }, [site, isoDate]);

  const run = useCallback(
    async (operation: () => Promise<Cart | null>) => {
      setBusy(true);
      setError(null);
      try {
        const next = await operation();
        setCart(next);
        // Any change to the cart invalidates the last cut-off re-check.
        setFlagged(new Map());
        return next;
      } catch (failure: unknown) {
        setError(failure instanceof Error && failure.message ? failure.message : t('hospitality.order.failed'));
        return null;
      } finally {
        setBusy(false);
      }
    },
    [t],
  );

  const addLine = useCallback(
    async (input: AddLineInput) => {
      if (!source?.addLine || !source.startCart) return;
      setDialogItem(null);
      await run(async () => {
        if (!cart) await source.startCart!(fulfilment);
        return source.addLine!(input);
      });
    },
    [cart, fulfilment, run, source],
  );

  const changeFulfilment = useCallback(
    async (next: DinerFulfilmentType) => {
      setFulfilment(next);
      setRequestedAt(null);
      if (!source?.setFulfilment || !cart) return;
      // Switching away from DELIVERY sends no address, and the backend clears
      // `Cart.deliveryAddress` and sets `collectionLocation` in one transaction.
      await run(() =>
        source.setFulfilment!({
          fulfilmentType: next,
          collectionLocationId: next === 'DELIVERY' ? null : (site?.locationId ?? null),
        }),
      );
    },
    [cart, run, site, source],
  );

  const submitAddress = useCallback(
    async (address: DeliveryAddressInput) => {
      if (!source?.setFulfilment) return;
      await run(() => source.setFulfilment!({ fulfilmentType: 'DELIVERY', deliveryAddress: address }));
    },
    [run, source],
  );

  /** The cut-off re-check. Nothing is removed; the failing lines are flagged. */
  const review = useCallback(async () => {
    if (!source || !cart) return;
    setBusy(true);
    setError(null);
    try {
      const ids = productIdsToRecheck(cart);
      const results = new Map<number, Orderability | null>();
      for (const productId of ids) {
        results.set(productId, await source.orderable(productId, at));
      }
      const problems = unorderableLines(cart, results);
      setFlagged(problems);
      if (problems.size === 0) setStage('checkout');
    } catch (failure: unknown) {
      console.error('[RestaurantOrder] orderable', failure);
      setError(t('hospitality.order.failed'));
    } finally {
      setBusy(false);
    }
  }, [at, cart, source, t]);

  const placeOrder = useCallback(
    async (input: CheckoutInput) => {
      if (!source?.checkout) return;
      setBusy(true);
      setError(null);
      try {
        // One key per attempt, re-sent unchanged on a retry: that is what makes
        // a retry idempotent instead of a second dinner.
        const placed = await source.checkout(input, idempotencyKey());
        if (placed) {
          setOrder(placed);
          setCart(null);
          setStage('placed');
        }
      } catch (failure: unknown) {
        setError(failure instanceof Error && failure.message ? failure.message : t('hospitality.order.failed'));
      } finally {
        setBusy(false);
      }
    },
    [source, t],
  );

  const title = heading ?? t('hospitality.order.heading');

  if (state.status !== 'ready' || !site) {
    return (
      <BlockSection heading={title}>
        <SiteStateNotice state={state} t={t} />
      </BlockSection>
    );
  }

  if (!ordering || menuGap) {
    return (
      <BlockSection heading={title}>
        <EmptyNote>{t('hospitality.order.empty')}</EmptyNote>
      </BlockSection>
    );
  }

  if (stage === 'placed' && order) {
    return (
      <BlockSection heading={title}>
        <Confirmation order={order} site={site} requestedAt={requestedAt} locale={locale} t={t} />
      </BlockSection>
    );
  }

  const currency = cart?.currency ?? menu?.currency ?? site.currency;

  return (
    <BlockSection heading={title}>
      <div className="grid gap-6 md:grid-cols-[3fr_2fr]">
        <div>
          {!menu || menu.sections.length === 0 ? (
            <EmptyNote>{t('hospitality.menu.empty')}</EmptyNote>
          ) : (
            menu.sections.map((section) => (
              <section key={`${section.categoryId ?? section.name}-${section.position}`} className="mb-6">
                <h3 className="text-base font-semibold">{section.name}</h3>
                <ul className="mt-2 divide-y">
                  {section.items.map((item) => (
                    <li key={item.productId} className="flex items-start justify-between gap-3 py-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium">{item.title}</p>
                        {item.description ? (
                          <p className="text-xs text-[var(--color-text-secondary)]">{item.description}</p>
                        ) : null}
                        {!item.orderable ? (
                          <p className="text-xs text-[var(--color-text-secondary)]">
                            {t('hospitality.order.unavailable')}
                          </p>
                        ) : null}
                      </div>
                      <div className="shrink-0 text-right">
                        <p className="text-sm">
                          {item.price
                            ? (formatMoney({ amount: item.price, currency }, locale) ?? item.price)
                            : ''}
                          {item.price && !item.taxInclusive ? (
                            <span className="ml-1 text-xs text-[var(--color-text-secondary)]">
                              {t('hospitality.order.plusTax')}
                            </span>
                          ) : null}
                        </p>
                        <button
                          type="button"
                          disabled={!item.orderable || busy}
                          onClick={() =>
                            item.modifierGroups.length > 0
                              ? setDialogItem(item)
                              : void addLine({ productId: item.productId, quantity: '1' })
                          }
                          className="mt-1 rounded border px-3 py-1 text-sm disabled:opacity-50"
                        >
                          {t('hospitality.order.add')}
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            ))
          )}
        </div>

        <aside aria-label={t('hospitality.order.cart')} className="rounded border p-4">
          <h3 className="text-base font-semibold">{t('hospitality.order.cart')}</h3>
          {cart ? (
            <CartPanel
              cart={cart}
              currency={currency}
              locale={locale}
              t={t}
              flagged={flagged}
              busy={busy}
              onQuantity={(lineId, quantity) =>
                void run(() => source!.setLineQuantity!(lineId, quantity))
              }
              onRemove={(lineId) => void run(() => source!.removeLine!(lineId))}
            />
          ) : (
            <p className="text-sm text-[var(--color-text-secondary)]">{t('hospitality.order.cartEmpty')}</p>
          )}

          {cart && isoDate ? (
            <FulfilmentControls
              site={site}
              cart={cart}
              choices={choices}
              fulfilment={fulfilment}
              requestedAt={requestedAt}
              isoDate={isoDate}
              nowMs={Date.now()}
              locale={locale}
              t={t}
              busy={busy}
              onFulfilment={(next) => void changeFulfilment(next)}
              onDeliveryAddress={(address) => void submitAddress(address)}
              onDate={(next) => {
                setIsoDate(next);
                setRequestedAt(null);
              }}
              onRequestedAt={setRequestedAt}
            />
          ) : null}

          {error ? (
            <p role="alert" className="mt-3 text-sm text-red-700">
              {error}
            </p>
          ) : null}

          {flagged.size > 0 ? (
            <p role="alert" className="mt-3 text-sm text-red-700">
              {t('hospitality.order.flaggedLines', { count: flagged.size })}
            </p>
          ) : null}

          {cart && stage === 'order' ? (
            <button
              type="button"
              disabled={busy || cart.lines.length === 0 || flagged.size > 0}
              onClick={() => void review()}
              className="mt-4 w-full rounded bg-black px-4 py-2 text-sm text-white disabled:opacity-50"
            >
              {t('hospitality.order.review')}
            </button>
          ) : null}

          {stage === 'checkout' ? (
            <CheckoutForm
              t={t}
              busy={busy}
              termsDocumentKey={termsDocumentKey}
              termsDocumentVersion={termsDocumentVersion}
              termsHref={termsHref}
              requestedAt={requestedAt}
              requestedAtLabel={
                requestedAt ? formatInZone(requestedAt, site.timeZone, locale) : null
              }
              error={error}
              onBack={() => setStage('order')}
              onSubmit={(input) => void placeOrder(input)}
            />
          ) : null}
        </aside>
      </div>

      {dialogItem ? (
        <ModifierDialog
          item={dialogItem}
          currency={currency}
          locale={locale}
          t={t}
          busy={busy}
          onCancel={() => setDialogItem(null)}
          onAdd={(input) => void addLine(input)}
        />
      ) : null}
    </BlockSection>
  );
}

function Confirmation({
  order,
  site,
  requestedAt,
  locale,
  t,
}: {
  order: PlacedOrder;
  site: RestaurantSite;
  requestedAt: string | null;
  locale: string;
  t: (key: string, params?: Record<string, string | number>) => string;
}) {
  return (
    <div>
      <p className="text-base font-semibold">
        {t('hospitality.order.placed', { number: order.orderNumber ?? '' })}
      </p>
      {requestedAt ? (
        // Formatted in the CHANNEL's timezone, not the visitor's: the kitchen's
        // clock is the one the promise was made on.
        <p className="mt-1 text-sm">
          {t('hospitality.order.promisedFor', {
            at: formatInZone(requestedAt, site.timeZone, locale),
          })}
        </p>
      ) : null}
      {order.total ? (
        <p className="mt-1 text-sm">
          {t('hospitality.order.total')}:{' '}
          {formatMoney({ amount: order.total, currency: order.currency ?? site.currency }, locale) ??
            order.total}
        </p>
      ) : null}
      <p className="mt-3 text-sm text-[var(--color-text-secondary)]">
        {t('hospitality.order.paymentAtVenue')}
      </p>
    </div>
  );
}

/** Today in a named zone as `YYYY-MM-DD`. `en-CA` formats exactly that way, and
 *  reading it back beats assembling one out of parts. */
function todayIn(timeZone: string): string {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

function idempotencyKey(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
